<template>
  <v-container fluid>
    <v-sheet :color="backgroundColor" :class="backgroundClass">

      <!-- Main Toolbar -->
      <v-toolbar dense flat dark :color="toolbarColor">
        <app-back-button v-bind:color="toolbarColorDark" />
        <v-toolbar-title>{{ activeResident.name }}</v-toolbar-title>
      </v-toolbar>

      <!-- Filters -->
      <v-toolbar dense flat class="mb-3">
        <v-spacer />

          <!-- For Period -->
          <span :class="filterLabelClass">{{ getLabel('ForPeriod') }}:</span>
          <v-toolbar-items>
            <v-select
              v-model="selectedTimePeriodValue"
              :items="languagedTimePeriods"
              item-value="value"
              item-text="label"
              @change="loadActivations = true"
              :disabled="isLoading"
              class="ma-2"
              hide-details
              >
          </v-select>
        </v-toolbar-items>
      </v-toolbar>

      <!-- Usage Charts -->
      <v-sheet :color="bannerColor" :class="bannerClass">{{ getHeading('GenieUsage') }}</v-sheet>
      <v-row justify="center" :class="rowClass">

        <!-- Users -->
        <v-col cols="12" sm="8" md="6">
          <mobile-users
            v-on:data-loaded="usersLoaded"
            :toolbarColor="toolbarColorExtraDark"
          />
        </v-col>

        <!-- Activations -->
        <v-col cols="12" sm="8" md="6">
          <dashboard-bar-chart
            v-on:data-loaded="activationsLoaded"
            ref="activationsChart"
            :trigger="loadActivations"
            :dataSource="'activation'"
            :selectedTimePeriodValue="selectedTimePeriodValue"
            :cardTitle="getExpression('FormTitle', 'RecentActivations')"
            :toolbarColor="toolbarColorExtraDark"
          />
        </v-col>
      </v-row>

      <!-- Messages Charts -->
      <v-sheet :color="bannerColor" :class="bannerClass">{{ getHeading('MetricsMessages') }}</v-sheet>
      <v-row justify="center" :class="rowClass">

        <!-- Received -->
        <v-col cols="12" sm="8" md="6">
          <dashboard-bar-chart
            v-on:data-loaded="activationsLoaded"
            :trigger="loadMessagesCharts"
            :dataSource="'messages'"
            :selectedTimePeriodValue="selectedTimePeriodValue"
            :messageDirection="'MessagesTo'"
            :cardTitle="messagesReceivedByUser"
            :toolbarColor="toolbarColorExtraDark"
          />
        </v-col>
        
        <!-- Sent -->
        <v-col cols="12" sm="8" md="6">
          <dashboard-bar-chart
            v-on:data-loaded="activationsLoaded"
            :trigger="loadMessagesCharts"
            :dataSource="'messages'"
            :selectedTimePeriodValue="selectedTimePeriodValue"
            :messageDirection="'MessagesFrom'"
            :cardTitle="messagesSentByUser"
            :toolbarColor="toolbarColorExtraDark"
          />
        </v-col>
      </v-row>

      <v-row justify="center" :class="rowClass">

        <!-- Received/Sent (Extended) -->
        <v-col cols="12" sm="12" class="pt-0">
          <messages-charts
            v-on:data-loaded="messagesLoaded"
            v-bind:users="userItems"
            :trigger="loadMessagesCharts"
            :selectedTimePeriodValue="selectedTimePeriodValue"
            :toolbarColor="toolbarColorExtraDark"
          />
        </v-col>
      </v-row>

      <!-- Memory Albums Charts -->
      <v-sheet :color="bannerColor" :class="bannerClass">{{ getHeading('MetricsMemoryAlbums') }}</v-sheet>
      <v-row justify="center" :class="rowClass">

        <!-- Albums -->
        <v-col cols="12" sm="8" md="6">
          <playlist-chart
            v-on:data-loaded="memoryAlbumsLoaded"
            :trigger="loadMemoryAlbums"
            :disableFilter="loadMediaUploads"
          />
        </v-col>

        <!-- Uploads -->
        <v-col cols="12" sm="8" md="6">
          <dashboard-bar-chart
            v-on:data-loaded="loadMediaUploads = false"
            :trigger="loadMediaUploads"
            :dataSource="'uploads'"
            :selectedTimePeriodValue="selectedTimePeriodValue"
            :cardTitle="getExpression('FormTitle', 'MediaUploads')"
            :hide="true"
            :toolbarColor="toolbarColorExtraDark"
          />
        </v-col>
      </v-row>

    </v-sheet>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  AppColor,
  KioskTypeId,
  MessageTypeId,
  DashboardStyles
} from '@/config.js'

// imports
import appService from '@/app.service.js'
import MobileUsers from '@/components/ResidentDashboard/MobileUsers'
import MessagesCharts from '@/components/ResidentDashboard/MessagesCharts'
import PlaylistChart from '@/components/ResidentDashboard/PlaylistChart'
import DashboardBarChart from '@/components/ResidentDashboard/DashboardBarChart'
import AppBackButton from '@/components/App/AppBackButton'

// constants
const BackgroundColor = '#edf2f2'
const BackgroundClass = 'elevation-1 pb-3'
const BannerClass = 'title font-weight-light text-center py-2 mt-1 mb-2'
const RowClass = 'mx-1'
const FilterLabelClass = 'mr-2 grey--text'

export default {
  name: 'ResidentMetrics',
  components: {
    MobileUsers,
    MessagesCharts,
    PlaylistChart,
    DashboardBarChart,
    AppBackButton
},
  data() {
    return {
      // colors
      appColor_Abby: AppColor.KioskTypeAbbyLight,
      appColor_Genie: AppColor.KioskTypeGenieLight,
      appColor_ResidentExtraLight: AppColor.ResidentExtraLight,
      appColor_ResidentLight: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,
      appColor_ResidentExtraDark: AppColor.ResidentExtraDark,
      bannerColor: AppColor.ResidentExtraLight,
      backgroundColor: BackgroundColor,
      backgroundClass: BackgroundClass,
      rowClass: RowClass,
      filterLabelClass: FilterLabelClass,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      // message types
      messageTypeId_UserToResident: MessageTypeId.UserToResident,
      messageTypeId_ResidentToUser: MessageTypeId.ResidentToUser,

      userItems: [],

      loadMessagesSummary: false,
      loadActivations: false,
      loadMessagesCharts: false,
      loadMemoryAlbums: false,
      loadMediaUploads: false,
      
      // chart 
      chartsContainer: DashboardStyles.ChartContainer,
      dataSource: 'activation',
      supportedTimePeriods: [],
      selectedTimePeriodValue: 'WeekToDate',
      isLoading: true
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'activeResident',
      'isAdmin',
      'isResidentManager',
      'isKioskManager',
      'previousRoute',
      'locale']),
    title () {
      const defaultTitle = this.getExpression('FormTitle', 'ResidentDashboard')
      if (this.isNullOrUndefined(this.activeResident)) return defaultTitle

      if (this.isGenie) {
        return this.getExpression('FormTitle', 'GenieResidentMetrics')
      } else if (this.isAbby) {
        return this.getExpression('FormTitle', 'AbbyResidentMetrics')
      } else return defaultTitle
    },
    isFromKioskDashboard () {
      return this.previousRoute.indexOf('KioskDashboard') >= 0
        || (this.previousRoute.indexOf('Home') >= 0 && this.isKioskManager)
    },
    kiosk () {
      const unknownKiosk = {
        id: 0,
        name: 'Unknown Kiosk',
        codeName: 'UNKNOWN',
        typeId: 0
      }
      if (this.isNullOrUndefined(this.activeResident)) return unknownKiosk
      return this.activeResident.kiosk
    },
    facility () {
      const unknownFacility = {
        id: 0,
        name: 'Unknown Facility',
        code: 'UNKNOWN',
        geographicalDescription: 'Unknown Location'
      }
      if (this.isNullOrUndefined(this.activeResident)) return unknownFacility
      return this.activeResident.kiosk.facility
    },
    hasMultipleResidents () {
      if (this.isNullOrUndefined(this.profile.residentIds)) return false
      return this.profile.residentIds.length > 1
    },
    isGenie () {
      return this.kiosk.typeId === this.kioskTypeId_Genie
    },
    isAbby () {
      return this.kiosk.typeId === this.kioskTypeId_Abby
    },
    languagedTimePeriods () {
      const self = this
      return self.supportedTimePeriods.filter(pl => {
        return pl.value === pl.value
      }).map(l => { return {
        value: l.value,
        label: this.getExpression('MenuItem', l.value),
        selected: l.selected
      }})
    },
    messagesReceivedByUser () {
      return this.getExpression('FormTitle', 'MessagesReceivedByUser').replace('{0}', this.activeResident.firstName)
    },
    messagesSentByUser () {
      return this.getExpression('FormTitle', 'MessagesSentByUser').replace('{0}', this.activeResident.firstName)
    },
    bannerClass () {
      return this.isSmallDevice
        ? `${BannerClass} body-1`
        : `${BannerClass}`
    },
    toolbarColor () {
      return this.appColor_ResidentLight
    },
    toolbarColorDark () {
      return this.appColor_ResidentDark
    },
    toolbarColorExtraDark () {
      return this.appColor_ResidentExtraDark
    }
  },
  methods: {
    loadData () {
      this.isLoading = true
      const self = this
      appService.getResidentDashboardBarChart(self.dataSource, self.activeResident.id, { timePeriod: self.selectedTimePeriodValue }, self.locale).then(data => {
        self.isLoading = false
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.supportedTimePeriods = data.supportedTimePeriods
          self.$emit('data-loaded')
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    getKioskTypeColor (typeId) {
      if (typeId === this.kioskTypeId_Abby) {
        return this.appColor_Abby
      } else if (typeId === this.kioskTypeId_Genie) {
        return this.appColor_Genie
      } else return 'grey'
    },
    
    getHeading (type) {
      return this.getExpression('Heading', type)
    },
    
    getLabel (type) {
      return this.getExpression('Label', type)
    },

    // events
    usersLoaded (userItems) {
      this.userItems = userItems
      this.loadActivations = true
    },

    activationsLoaded () {
      this.loadMessagesCharts = true
      this.loadActivations = false
    },

    messagesLoaded () {
      this.loadMessagesCharts = false
      this.loadMemoryAlbums = true
    },

    memoryAlbumsLoaded () {
      this.loadMemoryAlbums = false
      this.loadMediaUploads = true
    }
  },
  created(){
    this.loadData()
  }
}
</script>
